import {
  useState,
  useMemo,
  createContext,
  Dispatch,
  SetStateAction,
  ReactNode,
  useContext,
} from 'react';
import { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';

import { STORAGE_KEY } from '@auth_module/constants/storageKeys';
import { logout as logoutService } from '@auth_module/services';

export interface IUser extends AxiosResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  created_at: string;
  updated_at: string;
  email_verified_at: boolean | null;
  account_id: string;
  adc_ui_url: string;
  email_verified: boolean;
  organization_id: string;
}

export interface IAuthContext {
  auth: {
    token: string;
    user: IUser | Record<string, never>;
    castedAdmin?: string;
  };
  showTermsOfService: boolean;
  setAuth: Dispatch<
    SetStateAction<{
      token: string;
      user: IUser | Record<string, never>;
      castedAdmin?: string;
    }>
  >;
  logout: () => Promise<void>;
  setShowTermsOfService: Dispatch<SetStateAction<boolean>>;
}

export const AuthContext = createContext<IAuthContext | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const token = Cookies.get(STORAGE_KEY.auth_token) || '';

  const [auth, setAuth] = useState({
    token: token,
    user: {},
  });
  const [showTermsOfService, setShowTermsOfService] = useState(false);

  const authContextValue = useMemo(() => {
    return { auth, setAuth, logout, showTermsOfService, setShowTermsOfService };
  }, [auth, token, setAuth, logout]);

  async function logout() {
    return await logoutService();
  }

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
}

export const useAuthContext = () => {
  return useContext(AuthContext) as IAuthContext;
};
